import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Library as LibraryIcon, Play, Clock, Star, History } from "lucide-react";
import { formatDuration, formatTime } from "../mock";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useCatalog } from "../context/CatalogContext";
import { usePlayer } from "../context/PlayerContext";
import { Button } from "../components/ui/button";
import BookCover from "../components/BookCover";
import api from "../api";
import { Progress } from "../components/ui/progress";

export default function Library() {
  const { t } = useLang();
  const { user } = useAuth();
  const { books: BOOKS } = useCatalog();
  const { playTrack } = usePlayer();
  const navigate = useNavigate();
  const [continues, setContinues] = useState([]);

  const myBooks = user ? BOOKS.filter((b) => user.library.includes(b.id)) : [];

  useEffect(() => {
    if (!user) return;
    api.get("/me/continue").then((r) => setContinues(r.data || [])).catch(() => {});
  }, [user]);

  const resume = (item) => {
    const book = item.book;
    const chapter = book.chapters?.find((c) => c.id === item.chapterId) || book.chapters?.[0];
    playTrack(book, chapter, false);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-float-up min-h-[60vh]">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-11 h-11 rounded-xl bg-brand-500/15 border border-brand-500/30 flex items-center justify-center">
          <LibraryIcon className="w-5 h-5 text-brand-400" />
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{t("library_title")}</h1>
          <p className="text-sm text-muted-foreground">{myBooks.length} {t("results")}</p>
        </div>
      </div>

      {continues.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-4 h-4 text-brand-400" />
            <h2 className="font-display text-xl font-semibold">{t("continue_listening")}</h2>
          </div>
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
            {continues.map((item) => {
              const pct = item.duration ? Math.min(100, (item.position / item.duration) * 100) : 0;
              const ch = item.book.chapters?.find((c) => c.id === item.chapterId);
              return (
                <div
                  key={item.book.id}
                  className="w-[280px] shrink-0 rounded-2xl border border-border bg-card/50 p-3 hover:border-brand-500/40 transition-colors cursor-pointer"
                  onClick={() => resume(item)}
                >
                  <div className="flex gap-3">
                    <BookCover book={item.book} className="w-14 h-20 rounded-lg cover-shadow shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{item.book.title_en}</p>
                      <p className="text-xs text-muted-foreground truncate">{ch?.title_en || item.book.author}</p>
                      <div className="flex items-center gap-1.5 mt-1 text-brand-400 text-xs font-medium">
                        <Play className="w-3 h-3" fill="currentColor" /> {formatTime(item.position)}
                      </div>
                    </div>
                  </div>
                  <Progress value={pct} className="h-1 mt-3" />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {myBooks.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-24 text-center">
          <LibraryIcon className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
          <p className="text-muted-foreground max-w-sm mx-auto">{t("library_empty")}</p>
          <Button
            onClick={() => navigate("/browse")}
            className="mt-6 bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-full px-7"
          >
            {t("hero_explore")}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {myBooks.map((b) => (
            <div
              key={b.id}
              className="group flex items-center gap-4 sm:gap-6 p-4 rounded-2xl border border-border bg-card/50 hover:border-brand-500/40 transition-colors"
            >
              <Link to={`/book/${b.slug || b.id}`} aria-label={b.title_en} className="shrink-0">
                <BookCover book={b} className="w-16 h-24 sm:w-20 sm:h-28 rounded-lg cover-shadow" />
              </Link>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base sm:text-lg truncate">
                  <Link to={`/book/${b.slug || b.id}`} className="group-hover:text-brand-400 transition-colors">
                    {b.title_en}
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground truncate">
                  <Link to={`/author/${encodeURIComponent(b.author)}`} className="hover:text-brand-400 transition-colors">
                    {b.author}
                  </Link>
                  {b.narrator && (
                    <>
                      {" · "}
                      <Link to={`/narrator/${encodeURIComponent(b.narrator)}`} className="hover:text-brand-400 transition-colors">
                        {b.narrator}
                      </Link>
                    </>
                  )}
                </p>
                <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1 text-brand-400">
                    <Star className="w-3 h-3" fill="currentColor" /> {b.rating}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {formatDuration(b.duration)}
                  </span>
                </div>
              </div>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  playTrack(b, b.chapters[0], false);
                }}
                className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-full h-11 px-5 shrink-0"
              >
                <Play className="w-4 h-4 sm:mr-1.5" fill="currentColor" />
                <span className="hidden sm:inline">{t("listen_now")}</span>
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
